import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiChangePassword, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Moon, ShieldCheck, AlertCircle } from "lucide-react";

export default function ChangePassword() {
    const navigate = useNavigate();
    const { user, refresh, logout } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const forced = !!user?.force_password_change;

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (next.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        if (next !== confirm) {
            setError("New passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await apiChangePassword(current, next);
            await refresh();
            toast.success("Password updated");
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="min-h-screen bg-ntt-surface flex flex-col"
            data-testid="change-password-page"
        >
            <header className="bg-[#070F26] text-white">
                <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#0072BC] to-[#003a63] flex items-center justify-center">
                            <Moon className="h-4 w-4" />
                        </div>
                        <div className="text-[13px] font-semibold">NightOwl</div>
                    </div>
                    <button
                        type="button"
                        onClick={logout}
                        data-testid="cp-signout-btn"
                        className="text-[12px] text-white/70 hover:text-white"
                    >
                        Sign out
                    </button>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="w-full max-w-[480px] animate-fade-up">
                    {forced && (
                        <div
                            data-testid="cp-forced-banner"
                            className="mb-4 flex items-start gap-2 text-[13px] text-[#8a5a00] bg-[#FFF7DC] border border-[#F5DE94] rounded-md px-3 py-2.5"
                        >
                            <ShieldCheck className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>
                                For security, please change the initial password
                                before continuing.
                            </span>
                        </div>
                    )}
                    <div className="bg-white border border-ntt-border rounded-xl p-8 shadow-[0_1px_2px_rgba(7,15,38,0.04),0_8px_24px_rgba(7,15,38,0.06)]">
                        <h1
                            className="text-2xl font-semibold text-[#070F26] mb-1"
                            data-testid="cp-title"
                        >
                            Change your password
                        </h1>
                        <p className="text-sm text-ntt-muted mb-6">
                            Signed in as{" "}
                            <span className="font-medium text-[#070F26]">
                                {user?.email}
                            </span>
                        </p>

                        <form
                            onSubmit={submit}
                            className="space-y-4"
                            data-testid="cp-form"
                        >
                            <div>
                                <Label htmlFor="cp-current" className="text-[12px] text-ntt-muted mb-1.5 block">
                                    Current password
                                </Label>
                                <Input
                                    id="cp-current"
                                    data-testid="cp-current-input"
                                    type="password"
                                    required
                                    value={current}
                                    onChange={(e) => setCurrent(e.target.value)}
                                    className="h-11"
                                    autoComplete="current-password"
                                />
                            </div>
                            <div>
                                <Label htmlFor="cp-new" className="text-[12px] text-ntt-muted mb-1.5 block">
                                    New password
                                </Label>
                                <Input
                                    id="cp-new"
                                    data-testid="cp-new-input"
                                    type="password"
                                    required
                                    value={next}
                                    onChange={(e) => setNext(e.target.value)}
                                    className="h-11"
                                    autoComplete="new-password"
                                />
                                <p className="text-[11px] text-ntt-muted mt-1">
                                    Minimum 8 characters.
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="cp-confirm" className="text-[12px] text-ntt-muted mb-1.5 block">
                                    Confirm new password
                                </Label>
                                <Input
                                    id="cp-confirm"
                                    data-testid="cp-confirm-input"
                                    type="password"
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="h-11"
                                    autoComplete="new-password"
                                />
                            </div>

                            {error && (
                                <div
                                    data-testid="cp-error"
                                    className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                                >
                                    <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="cp-submit-btn"
                                className="w-full h-11 bg-[#0072BC] hover:bg-[#005a94] text-white font-medium"
                            >
                                {submitting ? "Updating…" : "Update password"}
                            </Button>
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
}
